var _react_refresh_temp_1, _react_refresh_temp_2;
var _react_refresh_temp_3;
_react_refresh_temp_3 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutationState } from '@tanstack/react-query';
import { useCallback } from 'react';
import { BadRequestError } from '@lucidlink/sdk/fetcher';
import { Box, Button, ButtonLink, Drawer, DrawerEmptyState, FolderIcon, IconBackground, InfinityIcon, LinkBroken02Icon, LoadingBox, Trans, translate, Typography, useModal, } from '@lucidlink/uicore';
import { SelectFolderModal } from '#components';
import { WEBSITE_CONTACT_ENTERPRISE_LINK } from '#constants';
import { DEPENDENCY_KEYS, useDependency } from '#context';
import { useWorkspaceById } from '#query-hooks';
import { checkIsBusiness, checkIsEnterprise } from '#services';
import { useCreateGuestLink } from '../../query-hooks/mutations/useCreateGuestLink';
import { useGetGuestLinksCapabilities } from '../../query-hooks/queries/useGetGuestLinksCapabilities';
/**
 * useMutationState only re-evaluates its filters on mutation cache events, so
 * remount the drawer when the tracked creation attempt changes.
 */
export const CreateGuestLinkDrawer = (props) => (_jsx(CreateGuestLinkDrawerContent, { ...props }, props.params.creationMutationKey));
_react_refresh_temp_1 = CreateGuestLinkDrawer;
$RefreshReg$(_react_refresh_temp_1, "CreateGuestLinkDrawer");
const CreateGuestLinkDrawerContent = ({ closeDrawer, isInert, params, }) => {
    _react_refresh_temp_3();
    const { creationMutationKey, filespaceId, workspaceId } = params;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- component
    const UpgradeButton = useDependency(DEPENDENCY_KEYS.BillingPlansLink);
    const { mutation: { mutate }, } = useCreateGuestLink();
    const creationStates = useMutationState({
        filters: {
            exact: true,
            mutationKey: [creationMutationKey],
            predicate: (mutation) => mutation.options.mutationKey?.at(0) === creationMutationKey,
        },
    });
    const creationState = creationStates.at(-1);
    const { query: { data: workspaceData }, } = useWorkspaceById(workspaceId);
    const { data: guestLinksCapabilities, isPending: isGuestLinksCapabilitiesPending, } = useGetGuestLinksCapabilities({
        workspaceId,
    });
    const guestLinkLimit = guestLinksCapabilities?.maxGuestLinks;
    /**
     * Business and Enterprise (every custom category, partner organizations
     * included) have no self-serve upgrade path — their guest link limit is
     * raised through sales.
     */
    const canSelfServeUpgrade = workspaceData !== undefined &&
        !checkIsBusiness(workspaceData.billing) &&
        !checkIsEnterprise(workspaceData.billing);
    // eslint-disable-next-line @typescript-eslint/naming-convention -- component
    const GuestLinkLimitButton = canSelfServeUpgrade ? (_jsx(UpgradeButton, { shortButtonText: true, size: "big", workspaceId: workspaceId })) : (_jsx(ButtonLink, { "data-testid": "contact-sales", kind: "primary", size: "big", target: "_blank", to: WEBSITE_CONTACT_ENTERPRISE_LINK, children: _jsx(Trans, { children: "Contact sales" }) }));
    const { closeModal, openModal } = useModal({
        closeOnUnmount: true,
        modalId: 'select-guest-link-folder',
    });
    const onSelectFolderClick = useCallback(() => {
        openModal({
            content: (_jsx(SelectFolderModal, { closeModal: closeModal, filespaceId: filespaceId, includeFiles: true, onSelect: ({ id }) => {
                    closeModal();
                    mutate({
                        filespaceId,
                        workspaceId,
                        fsEntryId: id,
                        origin: 'createGuestLinkButton',
                    });
                }, workspaceId: workspaceId })),
        });
    }, [closeModal, filespaceId, mutate, openModal, workspaceId]);
    const onRetryClick = useCallback(() => {
        const variables = creationState?.variables;
        if (variables) {
            mutate(variables);
        }
    }, [creationState?.variables, mutate]);
    /**
     * No creationState for a given key means the tracked mutation was
     * garbage-collected (it loses its observer once settled) or the drawer was
     * restored from the URL after a reload — start over instead of loading
     * forever. A just-started mutation is always observable: openDrawer runs
     * in onMutate, after the mutation is added to the cache.
     */
    if (!creationMutationKey || !creationState) {
        return (_jsx(Drawer, { closeButtonAriaLabel: translate('Close create guest link'), closeDrawer: () => void closeDrawer(), isInert: isInert, title: translate('Create guest link'), children: _jsxs(Box, { align: "center", gap: "l", isFullHeight: true, isFullWidth: true, justify: "center", orientation: "vertical", children: [_jsxs(Box, { align: "center", gap: "m", orientation: "vertical", children: [_jsx(IconBackground, { "data-testid": "folder-icon", Icon: FolderIcon, size: "large" }), _jsx(Typography, { kind: "p-large", children: _jsx(Trans, { children: "Select the file or folder you would like to share." }) })] }), _jsx(Button, { "data-testid": "select-folder", kind: "primary", onClick: onSelectFolderClick, children: _jsx(Trans, { children: "Select file or folder" }) })] }) }));
    }
    if (!creationState ||
        creationState.status === 'pending' ||
        isGuestLinksCapabilitiesPending) {
        return (_jsx(Drawer, { closeButtonAriaLabel: translate('Close create guest link'), closeDrawer: closeDrawer, isInert: isInert, title: translate('Create guest link'), children: _jsx(LoadingBox, { alt: translate('Creating guest link'), "data-testid": "creating-guest-link", isFullHeight: true, isFullWidth: true }) }));
    }
    if (creationState.status === 'error') {
        return (_jsxs(Drawer, { closeButtonAriaLabel: translate('Close create guest link'), closeDrawer: closeDrawer, isInert: isInert, title: translate('Create guest link'), children: [isLimitReachedError(creationState.error) ? (_jsx(DrawerEmptyState, { color: "indigo", "data-testid": "guest-link-limit-reached", Icon: InfinityIcon, message: translate('You’ve reached the limit of {{limit}} active guest links in your filespace. Upgrade to get more guest links.', { limit: guestLinkLimit }), children: GuestLinkLimitButton })) : (_jsx(DrawerEmptyState, { color: "red", "data-testid": "create-guest-link-error", Icon: LinkBroken02Icon, message: _jsx(Trans, { children: "An error occurred. The guest link wasn't created." }), children: _jsx(Button, { "data-testid": "retry-create-guest-link", kind: "primary", onClick: onRetryClick, size: "big", children: _jsx(Trans, { children: "Retry" }) }) })), _jsxs(Box, { gap: "s", isFullWidth: true, justify: "end", children: [_jsx(Button, { "data-testid": "guest-link-close", kind: "secondary", onClick: closeDrawer, size: "big", children: _jsx(Trans, { children: "Close" }) }), _jsx(Button, { "data-testid": "guest-link-copy", disabled: true, kind: "primary", size: "big", children: _jsx(Trans, { children: "Copy guest link" }) })] })] }));
    }
    return null;
};
_react_refresh_temp_2 = CreateGuestLinkDrawerContent;
$RefreshReg$(_react_refresh_temp_2, "CreateGuestLinkDrawerContent");
_react_refresh_temp_3(CreateGuestLinkDrawerContent, "rFEs65uofVJdVwmkFas2dHziWhc=", false, () => [useDependency, useCreateGuestLink, useMutationState, useWorkspaceById, useGetGuestLinksCapabilities, useModal]);
/** The code is not emitted by WebService yet — reserved for the upcoming guest-link limit enforcement. */
const isLimitReachedError = (error) => error instanceof BadRequestError &&
    error.errors[0]?.code === 'guestLink.limit.reached';
